/**
 * Generic Clustering Service
 * 
 * Provides LLM-based clustering functionality for both copy theme analysis
 * and product breakdown tabs. Completely generic and brand-agnostic.
 */

class GenericClusteringService {
  constructor() {
    this.baseUrl = '/api';
  }

  /**
   * Get ad data for clustering analysis
   * @param {string} accountId - Account ID
   * @param {string} dateRange - SQL date range condition
   * @param {Object} adNameFilter - Ad name filter object
   * @param {string} clusteringType - 'copy' or 'url' clustering
   * @returns {Promise<Object>} Ad data with performance metrics
   */
  async getAdData(accountId, dateRange, adNameFilter, clusteringType = 'copy') {
    const endpoint = clusteringType === 'url' ? '/clustering/url-data' : '/clustering/copy-data';
    
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        accountId,
        dateRange,
        adNameFilter
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch ad data: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Discover patterns using LLM for dynamic clustering
   * @param {Array} adData - Array of ad data
   * @param {string} clusteringType - 'copy' or 'url' clustering
   * @returns {Promise<Array>} Discovered patterns
   */
  async discoverPatterns(adData, clusteringType = 'copy') {
    const endpoint = clusteringType === 'url' ? '/clustering/discover-url-patterns' : '/clustering/discover-copy-patterns';
    
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ adData })
    });

    if (!response.ok) {
      throw new Error(`Failed to discover patterns: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Cluster ads using LLM-based classification
   * @param {Array} adData - Array of ad data
   * @param {Array} patterns - Discovered patterns
   * @param {string} clusteringType - 'copy' or 'url' clustering
   * @returns {Promise<Object>} Clustered data
   */
  async clusterAds(adData, patterns, clusteringType = 'copy') {
    const endpoint = clusteringType === 'url' ? '/clustering/cluster-by-url' : '/clustering/cluster-by-copy';
    
    console.log('🔍 API Call - Sending:', adData.length, 'ads to', endpoint);
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ adData, patterns })
    });

    if (!response.ok) {
      throw new Error(`Failed to cluster ads: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Get creative data for ads
   * @param {Array} adIds - Array of ad IDs
   * @returns {Promise<Array>} Creative data
   */
  async getCreativeData(adIds) {
    const response = await fetch(`${this.baseUrl}/clustering/creative-data`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ adIds })
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch creative data: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Get top performing ads for a cluster by selected metric
   * @param {Array} adIds - Array of ad IDs in the cluster
   * @param {number} limit - Maximum number of ads to return (default: 3)
   * @param {string} metric - Metric to sort by ('roas', 'revenue', 'spend', 'ctr') (default: 'roas')
   * @param {number|object} dateRange - Date range filter (number for days, or object with dateRangeType, startDate, endDate)
   * @param {object} filters - Optional filters object with dateRangeType, startDate, endDate, adNameFilter
   * @returns {Promise<Array>} Top performing ads with creative data
   */
  async getTopAdsByMetric(adIds, limit = 3, metric = 'roas', dateRange = null, filters = null) {
    // Support both legacy (dateRange as number) and new (filters object) format
    const requestBody = {
      adIds,
      limit,
      metric,
      dateRange: typeof dateRange === 'number' ? dateRange : null
    };
    
    // If filters object is provided, use it for date range and ad name filter
    if (filters) {
      requestBody.dateRangeType = filters.dateRangeType;
      requestBody.startDate = filters.startDate;
      requestBody.endDate = filters.endDate;
      requestBody.adNameFilter = filters.adNameFilter;
    } else if (typeof dateRange === 'object' && dateRange !== null) {
      // Legacy support: if dateRange is an object, treat it as filters
      requestBody.dateRangeType = dateRange.dateRangeType;
      requestBody.startDate = dateRange.startDate;
      requestBody.endDate = dateRange.endDate;
      requestBody.adNameFilter = dateRange.adNameFilter;
    }
    
    const response = await fetch(`${this.baseUrl}/clustering/top-roas-ads`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch top ${metric} ads: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Analyze copy content for clusters
   * @param {Array} clusters - Array of clusters
   * @param {Array} creativeData - Creative data for ads
   * @returns {Promise<Array>} Copy analysis results
   */
  async analyzeCopyContent(clusters, creativeData) {
    const response = await fetch(`${this.baseUrl}/clustering/analyze-copy`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clusters, creativeData })
    });

    if (!response.ok) {
      throw new Error(`Failed to analyze copy content: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Extract date range days from SQL date range condition
   * @param {string} dateRange - SQL date range condition
   * @returns {number} Number of days
   */
  extractDateRangeDays(dateRange) {
    if (!dateRange) return 7; // default
    
    // Extract days from INTERVAL 'X days' pattern
    const match = dateRange.match(/INTERVAL\s+'(\d+)\s+days'/i);
    if (match) {
      return parseInt(match[1]);
    }
    
    // If it's a custom date range with >= and <= pattern
    if (dateRange.includes('>=') && dateRange.includes('<=')) {
      const dateMatch = dateRange.match(/>=\s+'([^']+)'.*<=\s+'([^']+)'/i);
      if (dateMatch) {
        const start = new Date(dateMatch[1] + 'T00:00:00');
        const end = new Date(dateMatch[2] + 'T00:00:00');
        const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
        console.log('📊 Extracted custom date range days:', days, 'from', dateMatch[1], 'to', dateMatch[2]);
        return days;
      }
    }
    
    // If it's a custom date range with BETWEEN pattern
    if (dateRange.includes('BETWEEN')) {
      const dateMatch = dateRange.match(/BETWEEN\s+'([^']+)'\s+AND\s+'([^']+)'/i);
      if (dateMatch) {
        const start = new Date(dateMatch[1] + 'T00:00:00');
        const end = new Date(dateMatch[2] + 'T00:00:00');
        const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
        console.log('📊 Extracted custom date range days:', days, 'from', dateMatch[1], 'to', dateMatch[2]);
        return days;
      }
    }
    
    console.log('⚠️ Could not extract date range from:', dateRange, '- using fallback 7 days');
    return 7; // fallback
  }

  /**
   * Get cached analysis result
   * @param {Object} cacheKey - Cache key object
   * @returns {Promise<Object>} Cached result
   */
  async getCachedAnalysis(cacheKey) {
    try {
      const response = await fetch(`${this.baseUrl}/cache/get`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cacheKey)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching cached analysis:', error);
      return { success: false, cached: false };
    }
  }

  /**
   * Cache analysis result
   * @param {Object} cacheKey - Cache key object
   * @param {Object} analysisData - Analysis data to cache
   * @param {number} generationTimeMs - Generation time in milliseconds
   * @returns {Promise<Object>} Cache result
   */
  async cacheAnalysis(cacheKey, analysisData, generationTimeMs) {
    try {
      const response = await fetch(`${this.baseUrl}/cache/set`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...cacheKey,
          analysisData,
          generationTimeMs
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error caching analysis:', error);
      return { success: false };
    }
  }

  /**
   * Get comprehensive clustering analysis with caching
   * @param {string} accountId - Account ID
   * @param {string} dateRange - SQL date range condition
   * @param {Object} adNameFilter - Ad name filter object
   * @param {string} clusteringType - 'copy' or 'url' clustering
   * @returns {Promise<Object>} Complete clustering analysis
   */
  async getComprehensiveAnalysis(accountId, dateRange, adNameFilter, clusteringType = 'copy') {
    try {
      // Calculate date range days for caching
      const dateRangeDays = this.extractDateRangeDays(dateRange);
      
      // Check cache first
      const cacheKey = {
        accountId,
        analysisType: `generic_${clusteringType}_clustering`,
        dateRangeDays,
        adNameFilter: adNameFilter || null
      };
      
      console.log('🔍 Checking cache for clustering analysis:', cacheKey);
      const cachedResult = await this.getCachedAnalysis(cacheKey);
      
      if (cachedResult.success && cachedResult.cached && cachedResult.data) {
        // Check if cached data contains errors or empty clusters
        const hasError = cachedResult.data.error || cachedResult.data.success === false;
        const hasValidClusters = cachedResult.data.clusters && cachedResult.data.clusters.length > 0;
        const hasAdsInClusters = hasValidClusters && cachedResult.data.clusters.some(cluster => 
          cluster.ads && cluster.ads.length > 0
        );
        
        if (!hasError && hasValidClusters && hasAdsInClusters) {
          console.log('✅ Using cached clustering analysis');
          return {
            ...cachedResult.data,
            fromCache: true,
            cacheMetadata: cachedResult.metadata
          };
        } else {
          console.log('⚠️ Cached data is invalid or empty, regenerating...');
        }
      }

      console.log('🔄 Cache miss, generating new clustering analysis...');
      const startTime = Date.now();

      // Get ad data
      const adData = await this.getAdData(accountId, dateRange, adNameFilter, clusteringType);
      
      if (!adData || adData.length === 0) {
        return {
          success: false,
          error: 'No ad data found for the specified criteria',
          clusters: [],
          insights: [],
          recommendations: []
        };
      }

      // Cluster ads directly (no pattern discovery needed)
      console.log('🔍 Clustering Service - Input:', adData.length, 'ads');
      const clusterResult = await this.clusterAds(adData, [], clusteringType);
      console.log('🔍 Clustering Service - Output:', clusterResult.clusters?.reduce((sum, cluster) => sum + (cluster.ads?.length || 0), 0) || 0, 'ads clustered');
      
      // Get creative data for all ads
      const allAdIds = adData.map(ad => ad.raw_ad_id);
      const creativeData = await this.getCreativeData(allAdIds);

      const result = {
        success: true,
        clusters: clusterResult.clusters,
        creativeData: creativeData,
        totalSamples: adData.length,
        insights: clusterResult.insights || [],
        recommendations: clusterResult.recommendations || []
      };

      // Cache the result
      const generationTime = Date.now() - startTime;
      await this.cacheAnalysis(cacheKey, result, generationTime);
      console.log(`💾 Cached clustering analysis (${generationTime}ms)`);

      return result;

    } catch (error) {
      console.error('Error in comprehensive clustering analysis:', error);
      return {
        success: false,
        error: error.message,
        clusters: [],
        insights: [],
        recommendations: []
      };
    }
  }
}

const genericClusteringService = new GenericClusteringService();
export default genericClusteringService;
